// src/pages/AttendancePage.js
//
// CORRECTIONS v4 :
//  • Navigation dates : flèches ← → en plus du bouton "Changer de date".
//  • Source : 'mobile_app', 'agenda_auto', 'agenda_attendance', 'agenda_card10'
//    → tous mappés à "App 📱".
//  • consumedFromCard=true → badge "Carte 10" orangé dans la colonne Formule.
//  • Déduplication renforcée : on fusionne par studentId pour éviter
//    les doublons (même personne, 3 registrations différentes).
//  • Données manquantes dans manual_attendance → affichage gracieux
//    (email de l'utilisateur connecté si disponible dans le record).
//
// v5 : VUE "PAR DATE" — bascule "Par cours" / "Par date" en haut de page.
//  • "Par date" : on choisit un jour (flèches ou sélecteur), la page liste
//    TOUS les cours ayant une séance ce jour-là, avec le nombre d'attendus /
//    présents / absents et un statut (pas saisi / partiel / complet).
//  • Cliquer sur un cours de cette liste ouvre directement la vue "Par cours"
//    habituelle, sur ce cours et cette date précise (au lieu de la prochaine
//    séance par défaut).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../firebase';
import {
  collection, getDocs, doc, getDoc, setDoc, deleteDoc,
  Timestamp, addDoc,
} from 'firebase/firestore';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, CircularProgress, Button, Grid,
  Card, CardContent, Divider, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, FormControl, InputLabel, Select,
  FormControlLabel, Switch, AppBar, Toolbar, IconButton, Tooltip, Badge,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Event as EventIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CalendarToday,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  PhoneAndroid as AppIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  CreditCard as CardIcon,
  Videocam as ImageYesIcon,
  VideocamOff as ImageNoIcon,
  HelpOutline as ImageUnknownIcon,
} from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/fr';
moment.locale('fr');

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

const toDateKey = (ds) => moment(ds).format('YYYY-MM-DD');

/** Icône compacte "droit à l'image" à afficher à côté du nom d'un participant */
const ImageConsentIcon = ({ consent }) => {
  if (consent === true) {
    return (
      <Tooltip title="Autorise l'utilisation de l'image">
        <ImageYesIcon fontSize="small" sx={{ color: '#2e7d32' }} />
      </Tooltip>
    );
  }
  if (consent === false) {
    return (
      <Tooltip title="N'autorise PAS l'utilisation de l'image">
        <ImageNoIcon fontSize="small" sx={{ color: '#c62828' }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Droit à l'image non renseigné">
      <ImageUnknownIcon fontSize="small" sx={{ color: '#9e9e9e' }} />
    </Tooltip>
  );
};

/** Retourne true si une inscription concerne ce cours */
const matchesCourse = (reg, courseId) =>
  (reg.courseId ?? '') === courseId ||
  (Array.isArray(reg.selectedCourseIds) && reg.selectedCourseIds.includes(courseId));

/**
 * Droit à l'image : source de vérité = persons[].imageConsent (par personne).
 * Fallback sur les anciens champs (imageRightsConsentAll / imageRightsConsent)
 * pour les inscriptions créées avant le passage au consentement par enfant.
 * Retourne true / false / null (non renseigné).
 */
const getPersonImageConsent = (person, reg) => {
  if (person && person.imageConsent !== undefined && person.imageConsent !== null) {
    return person.imageConsent;
  }
  if (reg?.imageRightsConsentAll !== undefined) return reg.imageRightsConsentAll;
  if (reg?.imageRightsConsent !== undefined) return reg.imageRightsConsent;
  return null;
};

/** Retourne true si une inscription couvre cette date */
const matchesDate = (reg, formattedDate) => {
  if (reg.selectedDate === formattedDate && !reg.allDates) return true;
  if (reg.allDates) return true;
  if (Array.isArray(reg.selectedDates) && reg.selectedDates.includes(formattedDate)) return true;
  if (reg.plan === 'carte10' && Array.isArray(reg.consumedLessons)) {
    return reg.consumedLessons.some((cl) => cl.dateKey === formattedDate);
  }
  return false;
};

/** Sources venant de l'app Flutter */
const APP_SOURCES = new Set([
  'mobile_app', 'agenda_auto', 'agenda_attendance', 'agenda_card10',
  'mobile_registration', 'mobile_card10',
]);

const sourceLabel = (source) => {
  if (APP_SOURCES.has(source)) return 'App';
  if (source === 'manual')     return 'Manuel';
  return 'Site';
};

const sourceIcon = (source) => {
  if (APP_SOURCES.has(source)) return <AppIcon fontSize="small" sx={{ color: '#7C3AED' }} />;
  return null;
};

const planColor = (plan) => {
  if (!plan || plan === 'manual' || plan === 'agenda') return 'default';
  if (plan === 'essai')   return 'primary';
  if (plan === 'unique')  return 'secondary';
  if (plan === 'carte10') return 'warning';
  return 'success';
};

// ──────────────────────────────────────────────────────────────────────────────
// Composant principal
// ──────────────────────────────────────────────────────────────────────────────

function AttendancePage() {
  const navigate = useNavigate();

  const [courses,              setCourses]              = useState([]);
  const [selectedCourseId,     setSelectedCourseId]     = useState('');
  const [selectedCourse,       setSelectedCourse]       = useState(null);
  // Liste triée des dates ISO du cours sélectionné
  const [sortedDates,          setSortedDates]          = useState([]);
  const [selectedDateIndex,    setSelectedDateIndex]    = useState(-1);
  const [attendees,            setAttendees]            = useState([]);
  const [attendance,           setAttendance]           = useState({});
  const [loading,              setLoading]              = useState(true);
  const [saving,               setSaving]               = useState(false);
  const [saveSuccess,          setSaveSuccess]          = useState(false);
  const [dateDialogOpen,       setDateDialogOpen]       = useState(false);
  const [dateLoading,          setDateLoading]          = useState(false);
  const [allStudents,          setAllStudents]          = useState([]);
  const [addStudentDialogOpen, setAddStudentDialogOpen] = useState(false);
  const [newStudent,           setNewStudent]           = useState({ firstName: '', lastName: '', birthDate: '', email: '', phone: '', planType: 'manual', addToAllDates: false });
  const [selectedStudentId,    setSelectedStudentId]    = useState('');
  const [deleteDialogOpen,     setDeleteDialogOpen]     = useState(false);
  const [studentToDelete,      setStudentToDelete]      = useState(null);

  // ── Vue "par date" : toutes les séances d'un même jour, tous cours confondus ──
  const [viewMode,             setViewMode]             = useState('course'); // 'course' | 'date'
  const [dateViewDateKey,      setDateViewDateKey]      = useState(() => moment().format('YYYY-MM-DD'));
  const [dateViewCourses,      setDateViewCourses]      = useState([]);
  const [dateViewLoading,      setDateViewLoading]      = useState(false);
  // Date précise à sélectionner quand on arrive sur un cours depuis la vue
  // par date (au lieu de la prochaine séance par défaut) — une ref, pas un
  // state, car elle est lue une seule fois par l'effet de chargement du cours
  // puis aussitôt remise à null : elle n'a pas besoin de déclencher de rendu.
  const pendingDateKeyRef      = useRef(null);

  // Date sélectionnée (ISO string)
  const selectedDate = selectedDateIndex >= 0 && selectedDateIndex < sortedDates.length
    ? sortedDates[selectedDateIndex]
    : null;

  // ── Chargement des cours ───────────────────────────────────────────────────

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDocs(collection(db, 'dance_courses'));
        setCourses(snap.docs.map(d => ({ id: d.id, title: d.data().title, dates: d.data().dates || [] })));
      } catch (e) {
        console.error('Erreur cours:', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDocs(collection(db, 'students'));
        setAllStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error('Erreur étudiants:', e); }
    };
    fetch();
  }, []);

  // ── Chargement cours sélectionné + initialisation des dates ───────────────

  useEffect(() => {
    if (!selectedCourseId) return;
    const fetch = async () => {
      setLoading(true);
      setAttendees([]);
      setAttendance({});
      setSortedDates([]);
      setSelectedDateIndex(-1);
      try {
        const snap = await getDoc(doc(db, 'dance_courses', selectedCourseId));
        if (!snap.exists()) return;
        const data   = snap.data();
        const course = { id: selectedCourseId, ...data };
        setSelectedCourse(course);

        // Trier les dates et trouver la prochaine
        const today  = moment().startOf('day');
        const sorted = [...(data.dates || [])]
          .map(d => ({ iso: moment(d.date ?? d).toISOString(), _m: moment(d.date ?? d) }))
          .sort((a, b) => a._m - b._m);
        const isos = sorted.map(d => d.iso);
        setSortedDates(isos);

        // Une date précise était demandée (clic depuis la vue "Par date") :
        // on la sélectionne au lieu de la prochaine séance par défaut.
        const wantedKey = pendingDateKeyRef.current;
        pendingDateKeyRef.current = null;
        const wantedIdx = wantedKey
          ? sorted.findIndex(d => d._m.format('YYYY-MM-DD') === wantedKey)
          : -1;

        if (wantedIdx >= 0) {
          setSelectedDateIndex(wantedIdx);
        } else {
          // Sélectionner la prochaine séance (ou la dernière)
          const futureIdx = sorted.findIndex(d => d._m.isSameOrAfter(today));
          setSelectedDateIndex(futureIdx >= 0 ? futureIdx : isos.length - 1);
        }
      } catch (e) {
        console.error('Erreur chargement cours:', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [selectedCourseId]);

  // ── Navigation entre les dates ─────────────────────────────────────────────

  const goPrev = () => {
    if (selectedDateIndex > 0) setSelectedDateIndex(i => i - 1);
  };
  const goNext = () => {
    if (selectedDateIndex < sortedDates.length - 1) setSelectedDateIndex(i => i + 1);
  };

  const handleDateSelect = (iso) => {
    const idx = sortedDates.indexOf(iso);
    if (idx >= 0) setSelectedDateIndex(idx);
    setDateDialogOpen(false);
  };

  // ── Vue "par date" : navigation et ouverture d'un cours ────────────────────

  const goDateViewPrevDay = () => setDateViewDateKey(k => moment(k).subtract(1, 'day').format('YYYY-MM-DD'));
  const goDateViewNextDay = () => setDateViewDateKey(k => moment(k).add(1, 'day').format('YYYY-MM-DD'));

  const openCourseFromDateView = (courseId, dateKey) => {
    if (courseId === selectedCourseId) {
      // Le cours est déjà chargé en mémoire (sortedDates) : pas besoin de
      // repasser par l'effet de chargement, on sélectionne l'index directement.
      const idx = sortedDates.findIndex(iso => moment(iso).format('YYYY-MM-DD') === dateKey);
      if (idx >= 0) setSelectedDateIndex(idx);
    } else {
      pendingDateKeyRef.current = dateKey;
      setSelectedCourseId(courseId);
    }
    setViewMode('course');
  };

  // ── Chargement participants pour la date sélectionnée ─────────────────────

  const fetchAttendees = useCallback(async () => {
    if (!selectedCourseId || !selectedDate) return;
    setDateLoading(true);
    try {
      const formattedDate = toDateKey(selectedDate);

      // Map studentId → attendee (déduplication par studentId)
      const byStudentId = new Map();
      // Map attendeeId → attendee (pour les manual sans studentId)
      const byId        = new Map();

      // ── 1. Registrations ──────────────────────────────────────────────────
      const regsSnap = await getDocs(collection(db, 'registrations'));
      regsSnap.forEach(docSnap => {
        const reg     = docSnap.data();
        const regId   = docSnap.id;
        const persons = reg.persons || [];

        if (!matchesCourse(reg, selectedCourseId)) return;
        if (!matchesDate(reg, formattedDate))       return;

        persons.forEach((person, idx) => {
          const sid = person.studentId || reg.targetStudentId || reg.appUserId || '';
          // Déduplication : un même studentId ne s'affiche qu'une fois
          const key = sid || `${regId}_${idx}`;
          if (byStudentId.has(key)) return;
          const att = {
            id:              `${regId}_${idx}`,
            registrationId:  regId,
            studentId:       sid,
            firstName:       person.firstName || reg.targetStudentName?.split(' ')[0] || '',
            lastName:        person.lastName  || (reg.targetStudentName?.split(' ').slice(1).join(' ') ?? ''),
            birthDate:       person.birthDate,
            plan:            reg.plan,
            planName:        reg.planName || reg.plan || 'N/A',
            contactInfo:     reg.contactInfo,
            parentInfo:      reg.parentInfo,
            source:          reg.source || 'site',
            consumedFromCard:false,
            imageConsent:    getPersonImageConsent(person, reg),
          };
          byStudentId.set(key, att);
          byId.set(att.id, att);
        });
      });

      // ── 2. manual_attendance ──────────────────────────────────────────────
      const manSnap = await getDocs(collection(db, `courses/${selectedCourseId}/manual_attendance`));
      manSnap.forEach(docSnap => {
        const data   = docSnap.data();
        if (data.date !== formattedDate && !data.allDates) return;

        const sid = data.studentId || data.appUserId || '';
        // Déduplication : ne pas afficher si déjà dans les registrations
        if (sid && byStudentId.has(sid)) return;

        const id = `manual_${docSnap.id}`;
        if (!byId.has(id)) {
          const att = {
            id,
            firstName:         data.firstName || '',
            lastName:          data.lastName  || '',
            birthDate:         data.birthDate,
            plan:              data.plan || 'manual',
            planName:          data.planName || 'Ajout manuel',
            manuallyAdded:     true,
            studentId:         sid,
            addedForAllDates:  data.allDates || false,
            manualAttendanceId:docSnap.id,
            source:            data.source || 'manual',
            contactInfo:       data.contactInfo || null,
            consumedFromCard:  false,
            // Pas d'inscription liée pour un ajout manuel : droit à l'image non renseigné
            // sauf s'il a été explicitement saisi sur le document manual_attendance.
            imageConsent:      data.imageConsent ?? null,
          };
          if (sid) byStudentId.set(sid, att);
          byId.set(id, att);
        }
      });

      const attendeesData = Array.from(byId.values());
      setAttendees(attendeesData);

      // ── 3. Charger les présences existantes ───────────────────────────────
      const recordsSnap = await getDocs(
        collection(db, `courses/${selectedCourseId}/attendance/${formattedDate}/records`)
      );

      // Les records sont indexés par studentId s'il existe, sinon par l'id
      // de l'attendee (même fallback que saveAttendance : att.studentId || attendeeId)
      const recordsByKey = {};
      recordsSnap.forEach(r => {
        recordsByKey[r.id] = r.data();
      });

      // Mapper records → attendees
      const newAttState = {};
      attendeesData.forEach(att => {
        const recordKey = att.studentId || att.id;
        const rec = recordsByKey[recordKey];
        if (rec !== undefined) {
          newAttState[att.id] = rec.present;
          // Enrichir l'attendee avec consumedFromCard si présent dans le record
          if (rec.consumedFromCard) {
            att.consumedFromCard = true;
            att.planName         = rec.planName || 'Carte 10 cours';
          }
        }
      });

      setAttendance(prev => ({ ...prev, [selectedDate]: newAttState }));
      // Forcer la mise à jour des attendees enrichis
      setAttendees([...attendeesData]);
    } catch (e) {
      console.error('Erreur participants:', e);
    } finally {
      setDateLoading(false);
    }
  }, [selectedCourseId, selectedDate]);

  useEffect(() => { fetchAttendees(); }, [fetchAttendees]);

  // ── Vue "par date" : cours ayant une séance ce jour-là, avec un aperçu des
  // présences déjà saisies (tous cours confondus) ───────────────────────────
  useEffect(() => {
    if (viewMode !== 'date') return;
    let cancelled = false;

    const run = async () => {
      setDateViewLoading(true);
      try {
        // Cours ayant une séance ce jour-là (avec horaire, pour tri/affichage)
        const matches = [];
        courses.forEach((c) => {
          (c.dates || []).forEach((d) => {
            if (moment(d.date ?? d).format('YYYY-MM-DD') !== dateViewDateKey) return;
            matches.push({
              courseId:  c.id,
              title:     c.title,
              startTime: d.startTime || null,
              endTime:   d.endTime   || null,
            });
          });
        });
        matches.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));

        if (matches.length === 0) {
          if (!cancelled) setDateViewCourses([]);
          return;
        }

        // Inscriptions chargées UNE SEULE FOIS pour toute la date (plutôt
        // qu'une lecture par cours) — même correspondance que fetchAttendees
        // (matchesCourse + matchesDate + déduplication par studentId), mais
        // sans construire les objets attendee complets : seul le total
        // attendu nous intéresse ici.
        const regsSnap = await getDocs(collection(db, 'registrations'));
        const regs = regsSnap.docs.map(d => d.data());

        const enriched = await Promise.all(matches.map(async (m) => {
          const expectedKeys = new Set();
          regs.forEach((reg) => {
            if (!matchesCourse(reg, m.courseId))        return;
            if (!matchesDate(reg, dateViewDateKey))     return;
            (reg.persons || []).forEach((person, idx) => {
              const sid = person.studentId || reg.targetStudentId || reg.appUserId || '';
              expectedKeys.add(sid || `${m.courseId}_${idx}`);
            });
          });

          try {
            const manSnap = await getDocs(collection(db, `courses/${m.courseId}/manual_attendance`));
            manSnap.forEach((docSnap) => {
              const data = docSnap.data();
              if (data.date !== dateViewDateKey && !data.allDates) return;
              const sid = data.studentId || data.appUserId || '';
              expectedKeys.add(sid || `manual_${docSnap.id}`);
            });
          } catch (e) {
            console.error('Erreur manual_attendance (vue par date):', e);
          }

          let present = 0;
          let absent  = 0;
          try {
            const recSnap = await getDocs(
              collection(db, `courses/${m.courseId}/attendance/${dateViewDateKey}/records`)
            );
            recSnap.forEach((r) => {
              const p = r.data().present;
              if (p === true)  present += 1;
              if (p === false) absent  += 1;
            });
          } catch (e) {
            console.error('Erreur records (vue par date):', e);
          }

          return { ...m, expectedCount: expectedKeys.size, present, absent };
        }));

        if (!cancelled) setDateViewCourses(enriched);
      } catch (e) {
        console.error('Erreur chargement vue par date:', e);
        if (!cancelled) setDateViewCourses([]);
      } finally {
        if (!cancelled) setDateViewLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [viewMode, dateViewDateKey, courses]);

  // Préremplir email/téléphone quand un élève existant est choisi dans le dialog d'ajout
  useEffect(() => {
    if (!selectedStudentId) return;
    const s = allStudents.find(st => st.id === selectedStudentId);
    if (s) {
      setNewStudent(prev => ({
        ...prev,
        email: s.contactInfo?.email || s.email || '',
        phone: s.contactInfo?.phone || s.phone || '',
      }));
    }
  }, [selectedStudentId, allStudents]);

  // ── Gestion présence ───────────────────────────────────────────────────────

  // Toggle Présent/Absent : newValue vaut true, false, ou null si l'admin
  // reclique le bouton déjà sélectionné (ToggleButtonGroup exclusive) —
  // dans ce cas on retire le choix (redevient "non renseigné").
  const handlePresenceToggle = (attendeeId, newValue) => {
    if (!selectedDate) return;
    setAttendance(prev => {
      const dateState = { ...(prev[selectedDate] || {}) };
      if (newValue === null) {
        delete dateState[attendeeId];
      } else {
        dateState[attendeeId] = newValue;
      }
      return { ...prev, [selectedDate]: dateState };
    });
  };

  const saveAttendance = async () => {
    if (!selectedCourseId || !selectedDate) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      const formattedDate  = toDateKey(selectedDate);
      const dateAttendance = attendance[selectedDate] || {};

      for (const attendeeId of Object.keys(dateAttendance)) {
        const isPresent = dateAttendance[attendeeId];
        const att       = attendees.find(a => a.id === attendeeId);
        if (!att) continue;

        const recordKey = att.studentId || attendeeId;
        // Email du contact : site (contactInfo.email) ou app/parent (parentInfo.parentEmail)
        const contactEmail = att.contactInfo?.email || att.parentInfo?.parentEmail || null;
        await setDoc(
          doc(db, `courses/${selectedCourseId}/attendance/${formattedDate}/records`, recordKey),
          {
            present:          isPresent,
            status:           isPresent ? 'present' : 'absent',
            studentId:        att.studentId || '',
            registrationId:   att.registrationId || '',
            firstName:        att.firstName || '',
            lastName:         att.lastName  || '',
            updatedFrom:      'admin_panel',
            source:           att.source || 'admin_panel',
            consumedFromCard: att.consumedFromCard || false,
            // Ajoutés pour le mail automatique de suivi d'essai (cf. sendTrialAttendanceFollowup)
            plan:             att.plan || '',
            planName:         att.planName || '',
            email:            contactEmail,
            timestamp:        Timestamp.now(),
          },
          { merge: true }
        );
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      console.error('Erreur sauvegarde:', e);
    } finally {
      setSaving(false);
    }
  };

  // ── Ajouter manuellement ───────────────────────────────────────────────────

  const addStudentManually = async () => {
    if (!selectedStudentId && (!newStudent.firstName || !newStudent.lastName)) {
      alert('Veuillez sélectionner un élève ou renseigner un nom et prénom.'); return;
    }
    try {
      let studentData = {};
      let studentId   = '';

      // contactInfo n'est créé que si au moins un des deux champs est rempli,
      // pour ne pas polluer les records avec un objet {email:null, phone:null}
      const contactInfo = (newStudent.email || newStudent.phone)
        ? { email: newStudent.email || null, phone: newStudent.phone || null }
        : null;

      if (selectedStudentId) {
        const snap = await getDoc(doc(db, 'students', selectedStudentId));
        if (snap.exists()) {
          const s     = snap.data();
          studentData = { firstName: s.firstName, lastName: s.lastName, birthDate: s.birthDate, studentId: selectedStudentId };
          studentId   = selectedStudentId;
          // Si on a rempli/modifié l'email ou le téléphone, on les sauvegarde
          // sur la fiche élève pour ne plus avoir à les ressaisir la prochaine fois
          if (contactInfo) {
            await setDoc(doc(db, 'students', selectedStudentId), { contactInfo }, { merge: true });
          }
        }
      } else {
        const sRef  = await addDoc(collection(db, 'students'), {
          firstName: newStudent.firstName, lastName: newStudent.lastName,
          birthDate: newStudent.birthDate || null,
          contactInfo,
          createdAt: Timestamp.now(),
        });
        studentData = { firstName: newStudent.firstName, lastName: newStudent.lastName, birthDate: newStudent.birthDate, studentId: sRef.id };
        studentId   = sRef.id;
      }

      const isTrial = newStudent.planType === 'essai';
      const manRef = await addDoc(
        collection(db, `courses/${selectedCourseId}/manual_attendance`),
        {
          ...studentData, contactInfo,
          plan:     isTrial ? 'essai' : 'manual',
          planName: isTrial ? "Cours d'essai" : 'Ajout manuel',
          date:     newStudent.addToAllDates ? null : toDateKey(selectedDate),
          allDates: newStudent.addToAllDates, source: 'manual', addedAt: Timestamp.now(),
        }
      );

      const newAtt = {
        id: `manual_${manRef.id}`, ...studentData,
        plan:     isTrial ? 'essai' : 'manual',
        planName: isTrial ? "Cours d'essai" : 'Ajout manuel',
        manuallyAdded: true,
        addedForAllDates: newStudent.addToAllDates, manualAttendanceId: manRef.id,
        source: 'manual', consumedFromCard: false, contactInfo,
      };
      setAttendees(prev => [...prev, newAtt]);
      setAttendance(prev => ({ ...prev, [selectedDate]: { ...(prev[selectedDate] || {}), [newAtt.id]: true } }));
      setSelectedStudentId('');
      setNewStudent({ firstName: '', lastName: '', birthDate: '', email: '', phone: '', planType: 'manual', addToAllDates: false });
      setAddStudentDialogOpen(false);
    } catch (e) {
      console.error('Erreur ajout élève:', e);
    }
  };

  // ── Supprimer ──────────────────────────────────────────────────────────────

  const deleteStudentFromCourse = async () => {
    if (!studentToDelete) return;
    try {
      if (studentToDelete.manuallyAdded && studentToDelete.manualAttendanceId) {
        await deleteDoc(doc(db, `courses/${selectedCourseId}/manual_attendance`, studentToDelete.manualAttendanceId));
      }
      setAttendees(prev => prev.filter(a => a.id !== studentToDelete.id));
      if (selectedDate && attendance[selectedDate]) {
        const upd = { ...attendance[selectedDate] };
        delete upd[studentToDelete.id];
        setAttendance(prev => ({ ...prev, [selectedDate]: upd }));
      }
      setDeleteDialogOpen(false);
      setStudentToDelete(null);
    } catch (e) {
      console.error('Erreur suppression:', e);
    }
  };

  // ── Compteurs ──────────────────────────────────────────────────────────────

  const dateAttendance = attendance[selectedDate] || {};
  const presentCount   = Object.values(dateAttendance).filter(v => v === true).length;
  const absentCount    = Object.values(dateAttendance).filter(v => v === false).length;
  const totalCount     = attendees.length;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 0 }}>
      {/* ── AppBar ──────────────────────────────────────────────────────────── */}
      <AppBar position="static" elevation={0} sx={{ backgroundColor: '#7C3AED', mb: 3 }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <EventIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Gestion des présences
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* ── Vue : par cours (habituelle) ou par date (tous cours d'un jour) ─── */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <ToggleButtonGroup
            exclusive
            value={viewMode}
            onChange={(e, val) => { if (val) setViewMode(val); }}
            size="small"
          >
            <ToggleButton value="course" sx={{ px: 3 }}>Par cours</ToggleButton>
            <ToggleButton value="date"   sx={{ px: 3 }}>Par date</ToggleButton>
          </ToggleButtonGroup>
        </Paper>

        {/* ── Vue "par date" : toutes les séances du jour, tous cours ────────── */}
        {viewMode === 'date' && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Tooltip title="Jour précédent">
                <IconButton
                  onClick={goDateViewPrevDay}
                  sx={{ border: '1px solid #e0e0e0', borderRadius: 2, '&:hover': { borderColor: '#7C3AED', color: '#7C3AED' } }}
                >
                  <PrevIcon />
                </IconButton>
              </Tooltip>

              <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1, textAlign: 'center' }}>
                {moment(dateViewDateKey).format('dddd D MMMM YYYY')}
              </Typography>

              <Tooltip title="Jour suivant">
                <IconButton
                  onClick={goDateViewNextDay}
                  sx={{ border: '1px solid #e0e0e0', borderRadius: 2, '&:hover': { borderColor: '#7C3AED', color: '#7C3AED' } }}
                >
                  <NextIcon />
                </IconButton>
              </Tooltip>

              <TextField
                type="date"
                size="small"
                value={dateViewDateKey}
                onChange={(e) => e.target.value && setDateViewDateKey(e.target.value)}
                sx={{ ml: 1 }}
              />
              <Button size="small" onClick={() => setDateViewDateKey(moment().format('YYYY-MM-DD'))}>
                Aujourd'hui
              </Button>
            </Box>

            {dateViewLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : dateViewCourses.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                Aucun cours prévu ce jour-là.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Cours</TableCell>
                      <TableCell>Horaire</TableCell>
                      <TableCell align="center">Attendu(s)</TableCell>
                      <TableCell align="center">Présent(s)</TableCell>
                      <TableCell align="center">Absent(s)</TableCell>
                      <TableCell align="center">Statut</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dateViewCourses.map((c) => {
                      const saisie = c.present + c.absent;
                      const done   = c.expectedCount > 0 && saisie >= c.expectedCount;
                      return (
                        <TableRow
                          key={c.courseId}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => openCourseFromDateView(c.courseId, dateViewDateKey)}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>{c.title}</TableCell>
                          <TableCell>
                            {c.startTime ? `${c.startTime}${c.endTime ? ` - ${c.endTime}` : ''}` : '—'}
                          </TableCell>
                          <TableCell align="center">{c.expectedCount}</TableCell>
                          <TableCell align="center">{c.present}</TableCell>
                          <TableCell align="center">{c.absent}</TableCell>
                          <TableCell align="center">
                            {saisie === 0 ? (
                              <Chip label="Pas encore saisi" size="small" color="warning" variant="outlined" />
                            ) : done ? (
                              <Chip label="Complet" size="small" color="success" />
                            ) : (
                              <Chip label="Partiel" size="small" color="warning" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => { e.stopPropagation(); openCourseFromDateView(c.courseId, dateViewDateKey); }}
                              sx={{ borderColor: '#7C3AED', color: '#7C3AED' }}
                            >
                              Voir les présences
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* ── Vue "par cours" (habituelle) ────────────────────────────────────── */}
        {viewMode === 'course' && (
          <>
        {/* ── Sélection du cours ──────────────────────────────────────────────── */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Sélectionner un cours
          </Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <FormControl fullWidth>
              <InputLabel>Cours</InputLabel>
              <Select
                value={selectedCourseId}
                label="Cours"
                onChange={(e) => setSelectedCourseId(e.target.value)}
              >
                {courses.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Paper>

        {/* ── Sélection de la date ─────────────────────────────────────────── */}
        {selectedCourseId && (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              {/* Titre + navigation flèches + bouton calendrier */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {/* Flèche précédente */}
                <Tooltip title="Séance précédente">
                  <span>
                    <IconButton
                      onClick={goPrev}
                      disabled={selectedDateIndex <= 0}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        '&:not(:disabled)': { borderColor: '#7C3AED', color: '#7C3AED' },
                      }}
                    >
                      <PrevIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Titre date */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1, textAlign: 'center' }}>
                  {selectedDate
                    ? `Séance du ${moment(selectedDate).format('dddd D MMMM YYYY')}`
                    : 'Sélectionner une date'}
                </Typography>

                {/* Flèche suivante */}
                <Tooltip title="Séance suivante">
                  <span>
                    <IconButton
                      onClick={goNext}
                      disabled={selectedDateIndex >= sortedDates.length - 1}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        '&:not(:disabled)': { borderColor: '#7C3AED', color: '#7C3AED' },
                      }}
                    >
                      <NextIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Bouton calendrier */}
                <Button
                  variant="outlined"
                  startIcon={<CalendarToday />}
                  onClick={() => setDateDialogOpen(true)}
                  sx={{ borderColor: '#7C3AED', color: '#7C3AED', ml: 1 }}
                >
                  Changer de date
                </Button>
              </Box>

              {/* Indicateur position dans les séances */}
              {sortedDates.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textAlign: 'center' }}>
                  Séance {selectedDateIndex + 1} / {sortedDates.length}
                </Typography>
              )}

              {/* Chips résumé */}
              {selectedDate && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip label={`${totalCount} participant(s) attendu(s)`} variant="outlined" />
                  <Chip label={`${presentCount} présent(s)`}              color="success" />
                  <Chip label={`${absentCount} absent(s)`}                color="error" />
                  <Chip label={`${totalCount - presentCount - absentCount} non renseigné(s)`} color="warning" />
                </Box>
              )}
            </Paper>

            {/* ── Tableau participants ──────────────────────────────────────── */}
            {selectedDate && (
              <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Participants — {moment(selectedDate).format('DD/MM/YYYY')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setAddStudentDialogOpen(true)}
                    sx={{ borderColor: '#7C3AED', color: '#7C3AED' }}
                  >
                    Ajouter
                  </Button>
                </Box>
                <Divider />

                {dateLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Participant</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Formule</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Présence</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {attendees.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#999' }}>
                                Aucun participant pour cette date.
                              </TableCell>
                            </TableRow>
                          ) : (
                            attendees.map(att => (
                              <TableRow key={att.id} hover>
                                {/* Nom */}
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography fontWeight="bold">
                                      {att.firstName} {att.lastName}
                                    </Typography>
                                    <ImageConsentIcon consent={att.imageConsent} />
                                  </Box>
                                  {att.birthDate && (
                                    <Typography variant="caption" color="text.secondary">
                                      {moment(att.birthDate).format('DD/MM/YYYY')}
                                    </Typography>
                                  )}
                                </TableCell>

                                {/* Formule */}
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Chip
                                      label={att.planName || att.plan || 'N/A'}
                                      color={planColor(att.plan)}
                                      size="small"
                                    />
                                    {/* Badge spécial si consommé via carte10 */}
                                    {att.consumedFromCard && (
                                      <Chip
                                        icon={<CardIcon fontSize="small" />}
                                        label="Via carte 10"
                                        size="small"
                                        sx={{
                                          backgroundColor: '#fff3e0',
                                          color: '#e65100',
                                          fontSize: '0.7rem',
                                          height: 20,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>

                                {/* Source */}
                                <TableCell>
                                  <Chip
                                    label={sourceLabel(att.source)}
                                    size="small"
                                    icon={sourceIcon(att.source) ?? undefined}
                                    variant="outlined"
                                    sx={APP_SOURCES.has(att.source) ? { borderColor: '#7C3AED', color: '#7C3AED' } : {}}
                                  />
                                </TableCell>

                                {/* Contact */}
                                <TableCell>
                                  <Typography variant="caption">
                                    {att.contactInfo?.email
                                      || att.parentInfo?.parentEmail
                                      || (APP_SOURCES.has(att.source) ? '(via app)' : '—')}
                                  </Typography>
                                </TableCell>

                                {/* Présence */}
                                <TableCell align="center">
                                  <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    value={
                                      dateAttendance[att.id] === true  ? 'present' :
                                      dateAttendance[att.id] === false ? 'absent'  : null
                                    }
                                    onChange={(e, newValue) => {
                                      const mapped = newValue === 'present' ? true
                                        : newValue === 'absent' ? false
                                        : null;
                                      handlePresenceToggle(att.id, mapped);
                                    }}
                                  >
                                    <ToggleButton
                                      value="present"
                                      sx={{
                                        px: 1.5,
                                        '&.Mui-selected': { backgroundColor: '#e8f5e9', color: '#2e7d32', '&:hover': { backgroundColor: '#d0ecd1' } },
                                      }}
                                    >
                                      <Tooltip title="Présent">
                                        <CheckIcon fontSize="small" />
                                      </Tooltip>
                                    </ToggleButton>
                                    <ToggleButton
                                      value="absent"
                                      sx={{
                                        px: 1.5,
                                        '&.Mui-selected': { backgroundColor: '#ffebee', color: '#c62828', '&:hover': { backgroundColor: '#fdd8db' } },
                                      }}
                                    >
                                      <Tooltip title="Absent">
                                        <CloseIcon fontSize="small" />
                                      </Tooltip>
                                    </ToggleButton>
                                  </ToggleButtonGroup>
                                </TableCell>

                                {/* Actions */}
                                <TableCell align="center">
                                  {att.manuallyAdded && (
                                    <Tooltip title="Supprimer">
                                      <IconButton size="small" color="error"
                                        onClick={() => { setStudentToDelete(att); setDeleteDialogOpen(true); }}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {saveSuccess && (
                      <Box sx={{ p: 2, backgroundColor: '#e8f5e9', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon sx={{ color: '#2e7d32' }} />
                        <Typography color="success.main">Présences enregistrées !</Typography>
                      </Box>
                    )}

                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={saveAttendance}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} /> : <CheckIcon />}
                        sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' }, minWidth: 220, py: 1.5 }}
                      >
                        {saving ? 'Enregistrement...' : 'Enregistrer les présences'}
                      </Button>
                    </Box>
                  </>
                )}
              </Paper>
            )}
          </>
        )}
          </>
        )}

        {/* ── Dialog : ajouter un élève ───────────────────────────────────────── */}
        <Dialog open={addStudentDialogOpen} onClose={() => setAddStudentDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#7C3AED', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonAddIcon sx={{ mr: 1 }} /> Ajouter un élève
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Élève existant</InputLabel>
              <Select value={selectedStudentId} label="Élève existant"
                onChange={(e) => setSelectedStudentId(e.target.value)}>
                <MenuItem value="">-- Nouvel élève --</MenuItem>
                {allStudents.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.firstName} {s.lastName}
                    {s.birthDate && ` (${moment(s.birthDate).format('DD/MM/YYYY')})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!selectedStudentId && (
              <>
                <TextField autoFocus margin="dense" label="Prénom *" fullWidth variant="outlined"
                  value={newStudent.firstName} onChange={(e) => setNewStudent({ ...newStudent, firstName: e.target.value })} sx={{ mb: 2 }} />
                <TextField margin="dense" label="Nom *" fullWidth variant="outlined"
                  value={newStudent.lastName} onChange={(e) => setNewStudent({ ...newStudent, lastName: e.target.value })} sx={{ mb: 2 }} />
                <TextField margin="dense" label="Date de naissance" type="date" fullWidth variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={newStudent.birthDate} onChange={(e) => setNewStudent({ ...newStudent, birthDate: e.target.value })} sx={{ mb: 2 }} />
              </>
            )}
            {/* Email / téléphone : utile pour les élèves essai ajoutés à la main
                (permet au mail de suivi automatique de leur être envoyé) */}
            <TextField margin="dense" label="Email" type="email" fullWidth variant="outlined"
              value={newStudent.email} onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
              helperText="Requis pour recevoir le mail de suivi si formule essai" sx={{ mb: 2 }} />
            <TextField margin="dense" label="Téléphone" fullWidth variant="outlined"
              value={newStudent.phone} onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })} />
            {/* Formule : détermine si cet ajout déclenche le mail de suivi essai */}
            <Typography variant="body2" sx={{ mb: 1, color: '#374151', fontWeight: 600 }}>Type d'ajout</Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={newStudent.planType}
              onChange={(e, val) => { if (val) setNewStudent({ ...newStudent, planType: val }); }}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="manual">Ajout standard</ToggleButton>
              <ToggleButton value="essai">Cours d'essai</ToggleButton>
            </ToggleButtonGroup>
            {newStudent.planType === 'essai' && !newStudent.email && (
              <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#c62828' }}>
                ⚠️ Sans email, le mail de suivi essai ne pourra pas être envoyé.
              </Typography>
            )}
            <FormControlLabel
              control={<Switch checked={newStudent.addToAllDates}
                onChange={(e) => setNewStudent({ ...newStudent, addToAllDates: e.target.checked })} />}
              label="Ajouter pour toutes les dates de ce cours"
              sx={{ mt: 3, display: 'block' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddStudentDialogOpen(false)}>Annuler</Button>
            <Button onClick={addStudentManually} variant="contained"
              disabled={!selectedStudentId && (!newStudent.firstName || !newStudent.lastName)}
              sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}>
              Ajouter
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Dialog : sélectionner une date ─────────────────────────────────── */}
        <Dialog open={dateDialogOpen} onClose={() => setDateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#7C3AED', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarToday sx={{ mr: 1 }} /> Sélectionner une date
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {sortedDates.map((iso, i) => {
                const m = moment(iso);
                return (
                  <Card key={i} sx={{
                    mb: 1, cursor: 'pointer',
                    border: selectedDate === iso ? '2px solid #7C3AED' : '1px solid #e0e0e0',
                    backgroundColor: selectedDate === iso ? '#f3e8fd' : 'white',
                  }} onClick={() => handleDateSelect(iso)}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography fontWeight={selectedDate === iso ? 'bold' : 'normal'}>
                            {m.format('dddd D MMMM YYYY')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            à {m.format('HH:mm')} — séance {i + 1}/{sortedDates.length}
                          </Typography>
                        </Box>
                        {m.isSame(moment(), 'day') && <Chip label="Aujourd'hui" size="small" color="primary" />}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDateDialogOpen(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>

        {/* ── Dialog : supprimer ─────────────────────────────────────────────── */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#f44336', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DeleteIcon sx={{ mr: 1 }} /> Supprimer un élève
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography>Êtes-vous sûr de vouloir supprimer cet élève du cours ?</Typography>
            {studentToDelete && (
              <Typography fontWeight="bold" sx={{ mt: 2 }}>
                {studentToDelete.firstName} {studentToDelete.lastName}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Cette action est irréversible.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
            <Button onClick={deleteStudentFromCourse} variant="contained" color="error" startIcon={<DeleteIcon />}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default AttendancePage;